import authService from './authService';
import {
  InventorySession,
  InventorySessionDetail,
  CreateInventorySessionData,
  ConferInventoryEntryData,
  InventorySessionSearchModel,
} from '../types/inventorySession';
import { ApiResponse, PaginatedResponse } from '../types/common';

// Raw shapes returned by the backend (camelCase)
interface RawInventorySession {
  inventorySessionId: number;
  title: string;
  sessionType?: number; // 0 = Produtos | 1 = Matérias-Primas
  createdBy: string | null;
  createdAt: string;
  isActive: boolean;
  totalEntries: number;
  conferredEntries: number;
  hasConferredEntries: boolean;
}

interface RawInventoryEntry {
  inventoryEntriesId: number;
  inventorySessionId: number;
  productId: number | null;
  stockQuantity: number | null;
  stockMovementId: number | null;
  conferredAt: string | null;
  conferredBy: string | null;
  productSku: string;
  productDescription: string | null;
  currentStockQuantity: number | null;
  // Campos de matéria-prima (sessões tipo 1)
  rawMaterialId?: number | null;
  rawMaterialCode?: string | null;
  rawMaterialUnit?: string | null;
  materialType?: number | null;
  itemType?: 'product' | 'raw_material';
  previousBalance: number | null;
  newBalance: number | null;
}

interface RawInventoryProviderGroup {
  providerId: number;
  providerLegalName: string;
  providerTradeName: string | null;
  entries: RawInventoryEntry[];
}

interface RawInventorySessionDetail {
  session: RawInventorySession;
  providers: RawInventoryProviderGroup[];
}

class InventorySessionService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private normalizeSession(raw: RawInventorySession): InventorySession {
    return {
      inventory_session_id: raw.inventorySessionId,
      title: raw.title,
      session_type: raw.sessionType ?? 0,
      created_by: raw.createdBy,
      created_at: raw.createdAt,
      is_active: raw.isActive,
      total_entries: raw.totalEntries,
      conferred_entries: raw.conferredEntries,
      has_conferred_entries: raw.hasConferredEntries,
    };
  }

  private normalizeDetail(raw: RawInventorySessionDetail): InventorySessionDetail {
    return {
      session: this.normalizeSession(raw.session),
      providers: raw.providers.map(pg => ({
        provider_id: pg.providerId,
        provider_legal_name: pg.providerLegalName,
        provider_trade_name: pg.providerTradeName,
        entries: pg.entries.map(e => ({
          inventory_entries_id: e.inventoryEntriesId,
          inventory_session_id: e.inventorySessionId,
          product_id: e.productId,
          stock_quantity: e.stockQuantity,
          stock_movement_id: e.stockMovementId,
          conferred_at: e.conferredAt,
          conferred_by: e.conferredBy,
          product_sku: e.productSku,
          product_description: e.productDescription,
          current_stock_quantity: e.currentStockQuantity,
          raw_material_id: e.rawMaterialId ?? null,
          raw_material_code: e.rawMaterialCode ?? null,
          raw_material_unit: e.rawMaterialUnit ?? null,
          material_type: e.materialType ?? null,
          item_type: e.itemType ?? (e.rawMaterialId != null ? 'raw_material' : 'product'),
          previous_balance: e.previousBalance,
          new_balance: e.newBalance,
        })),
      })),
    };
  }

  /**
   * Lista sessões de inventário com paginação
   * GET /api/inventory-sessions
   */
  async getSessions(searchModel: InventorySessionSearchModel): Promise<PaginatedResponse<InventorySession>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
    });
    if (searchModel.isActive !== undefined) {
      params.append('is_active', searchModel.isActive.toString());
    }
    if (searchModel.orderBy) params.append('order_by', searchModel.orderBy);
    if (searchModel.orderDirection) params.append('order_direction', searchModel.orderDirection);

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/inventory-sessions?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar sessões de inventário');
    const result: PaginatedResponse<RawInventorySession> = await response.json();
    return {
      ...result,
      data: (result.data ?? []).map(s => this.normalizeSession(s)),
    };
  }

  /**
   * Obtém detalhes de uma sessão de inventário (incluindo entries agrupadas por fornecedor)
   * GET /api/inventory-sessions/:id
   */
  async getSessionDetail(sessionId: number): Promise<InventorySessionDetail> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/inventory-sessions/${sessionId}`);
    if (!response.ok) throw new Error('Falha ao buscar detalhes da sessão de inventário');
    const result: ApiResponse<RawInventorySessionDetail> = await response.json();
    return this.normalizeDetail(result.data!);
  }

  /**
   * Cria uma nova sessão de inventário (popula InventoryEntries automaticamente)
   * POST /api/inventory-sessions
   * sessionType: 0 = Produtos (default) | 1 = Matérias-Primas
   */
  async createSession(data: CreateInventorySessionData): Promise<InventorySession> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/inventory-sessions', {
      title: data.title,
      sessionType: data.session_type ?? 0,
    });
    if (!response.ok) throw new Error('Falha ao criar sessão de inventário');
    const result: ApiResponse<RawInventorySession> = await response.json();
    return this.normalizeSession(result.data!);
  }

  /**
   * Exclui uma sessão de inventário (somente se nenhum entry foi conferido)
   * DELETE /api/inventory-sessions/:id
   */
  async deleteSession(sessionId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/inventory-sessions/${sessionId}`);
    if (!response.ok) {
      const errBody = await response.json().catch(() => null);
      const msg = errBody?.message || errBody?.error || 'Falha ao excluir sessão de inventário';
      throw new Error(msg);
    }
  }

  /**
   * Registra a conferência de um item do inventário
   * POST /api/inventory-sessions/:sessionId/entries/:entryId/confer
   */
  async conferEntry(sessionId: number, data: ConferInventoryEntryData): Promise<InventorySessionDetail> {
    const apiClient = await this.getApiClient();
    const payload: Record<string, unknown> = {
      createStockMovement: data.create_stock_movement,
    };
    if (data.create_stock_movement && data.new_balance !== undefined) {
      payload.newBalance = data.new_balance;
    }
    const response = await apiClient.post(
      `/inventory-sessions/${sessionId}/entries/${data.inventory_entries_id}/confer`,
      payload
    );
    if (!response.ok) {
      const errBody = await response.json().catch(() => null);
      const msg = errBody?.message || errBody?.error || 'Falha ao registrar conferência';
      throw new Error(msg);
    }
    const result: ApiResponse<RawInventorySessionDetail> = await response.json();
    return this.normalizeDetail(result.data!);
  }

  /**
   * Encerra a sessão de inventário (marca IsActive = 0)
   * POST /api/inventory-sessions/:id/close
   */
  async closeSession(sessionId: number): Promise<InventorySessionDetail> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/inventory-sessions/${sessionId}/close`, {});
    if (!response.ok) {
      const errBody = await response.json().catch(() => null);
      const msg = errBody?.message || errBody?.error || 'Falha ao encerrar sessão de inventário';
      throw new Error(msg);
    }
    const result: ApiResponse<RawInventorySessionDetail> = await response.json();
    return this.normalizeDetail(result.data!);
  }

  /**
   * Gera PDF do relatório de ajustes do inventário finalizado
   * GET /api/inventory-sessions/:id/report/pdf
   */
  async downloadReportPdf(sessionId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/inventory-sessions/${sessionId}/report/pdf`);
    if (!response.ok) throw new Error('Falha ao gerar PDF do relatório de inventário');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventario-${sessionId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}

export const inventorySessionService = new InventorySessionService();
